/**
 * ЛОГИКА ЭКРАНА «НАГРАДЫ» · Ф2.4 — плотный список со шкалами (характер «дата-про»).
 *
 * Каталог наград и весь счёт живут в ядре (`core/achievements.py`): движок сам считает
 * метрику по `rule_type` и сам начисляет по `threshold`. Витрина не проверяет ни одного
 * условия — она берёт `GET /achievements` (значение · порог · получено) и раскладывает.
 *
 * ⭐ Единственное, что витрина делает сама, — ГОВОРИТ ПО-РУССКИ. В базе описание написано
 * в прошедшем времени и от лица движка («Занимался 3 дня подряд») — для НЕполученной
 * награды это неправда: человек ещё не занимался. Поэтому «как получить» собирается из
 * пары `rule_type` + `threshold`, то есть из САМОГО правила, которое прислало ядро;
 * выдуманного порога здесь появиться неоткуда. Полученная награда показывает своё
 * описание из базы как есть (оно приезжает в `earned`).
 *
 * ⚠ Долг, который витрина закрыть не может: серию дней (`streak_days`) ядро считает по
 * UTC-дате (`achievements._streak_days`), а не по `settings.tz`. На краю суток цифра может
 * отличаться на день. Правка — в ядре, вынесена в план окна.
 *
 * Ни DOM, ни сети — правила проверяются запуском:
 *   cd Lang/web && node tools/check-screens.mjs
 */

/** Строка каталога с прогрессом — ровно как её отдаёт `achievements.get_progress`. */
export interface AchProgress {
  code: string;
  title: string;
  rule_type: string;
  value: number;
  threshold: number;
  earned: boolean;
}

/** Начисленная награда — `achievements.get_user_achievements` (описание есть только здесь). */
export interface AchEarned {
  code: string;
  title: string;
  description: string;
  earned_at: string;
}

/** Значок — украшение, а не данные: подбирается по коду, запасной — по типу правила. */
const ICON_BY_CODE: Record<string, string> = {
  words_10: "🌱", words_50: "📗", words_100: "📚",
  known_10: "🧠", known_50: "🎓",
  reviews_50: "🏃", reviews_200: "🏅",
  streak_3: "🔥", streak_7: "📅", streak_30: "🗓",
  video_20: "🎬", own_20: "✍️", subgroup_1: "🎯",
};

const ICON_BY_RULE: Record<string, string> = {
  words_added: "📚", known_count: "🧠", reviews_total: "🏅",
  streak_days: "🔥", words_from_source: "✍️", subgroup_mastered: "🎯",
};

export function icon(a: { code: string; rule_type: string }): string {
  return ICON_BY_CODE[a.code] ?? ICON_BY_RULE[a.rule_type] ?? "🏆";
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** В чём меряется награда — чтобы «осталось 3» превратилось в «осталось 3 дня». */
export function unit(ruleType: string, n: number): string {
  switch (ruleType) {
    case "reviews_total":
      return plural(n, "ответ", "ответа", "ответов");
    case "streak_days":
      return plural(n, "день", "дня", "дней");
    case "subgroup_mastered":
      return plural(n, "набор", "набора", "наборов");
    default:                       // words_added · known_count · words_from_source
      return plural(n, "слово", "слова", "слов");
  }
}

/**
 * «Как получить» — пересказ правила ядра человеческим языком.
 * Порог всегда берётся из `threshold`, ничего не дописываем от себя.
 */
export function howTo(a: AchProgress): string {
  const n = a.threshold;
  switch (a.rule_type) {
    case "words_added":
      return `Набрать ${n} ${unit(a.rule_type, n)} в словаре — они приходят наборами ` +
             "и добавляются вручную.";
    case "known_count":
      return `Довести ${n} ${unit(a.rule_type, n)} до «знаю»: слово считается выученным, ` +
             "когда уходит на повтор через 30 дней.";
    case "reviews_total":
      return `Дать ${n} ${unit(a.rule_type, n)} на карточки — засчитывается любой, ` +
             "даже «не помню».";
    case "streak_days":
      return `Заниматься ${n} ${unit(a.rule_type, n)} подряд. Пропуск обнуляет серию, ` +
             "но не выученные слова.";
    case "words_from_source":
      return "Добавить 20 своих слов — вручную или из расшифровки своего видео.";
    case "subgroup_mastered":
      return "Довести до «знаю» все слова одного набора — целиком, без хвостов.";
    default:
      return `Дойти до ${n}.`;
  }
}

/** Доля выполнения, 0…100. Полученная награда — всегда полная полоса. */
export function pct(a: AchProgress): number {
  if (a.earned) return 100;
  if (!a.threshold || a.threshold <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((a.value / a.threshold) * 100)));
}

/** Сколько ещё нужно. Для полученной — ноль (значение может и перевалить порог). */
export function left(a: AchProgress): number {
  if (a.earned) return 0;
  return Math.max(0, a.threshold - a.value);
}

/** «осталось 12 дней» / «осталось 118 ответов» — одной строкой, в правильном падеже. */
export function leftLabel(a: AchProgress): string {
  const n = left(a);
  return `осталось ${n} ${unit(a.rule_type, n)}`;
}

/** Счётчик справа: у полученной — галочка, у будущей — «сколько из скольких». */
export function counter(a: AchProgress): string {
  return a.earned ? "✓" : `${Math.min(a.value, a.threshold)} / ${a.threshold}`;
}

/**
 * ⭐ Полученные и будущие — РАЗДЕЛЬНО (требование окна).
 * Полученные идут в порядке каталога (как их задумало ядро), будущие — по близости:
 * первой стоит та, до которой ближе всего, а при равной доле — с меньшим порогом.
 */
export function split(catalog: AchProgress[]): { earned: AchProgress[]; upcoming: AchProgress[] } {
  const earned = (catalog || []).filter((a) => a.earned);
  const upcoming = (catalog || []).filter((a) => !a.earned)
    .sort((a, z) => (pct(z) - pct(a)) || (a.threshold - z.threshold));
  return { earned, upcoming };
}

/** Самая близкая будущая награда — её выносим отдельной плашкой «до чего рукой подать». */
export function nearest(catalog: AchProgress[]): AchProgress | null {
  const { upcoming } = split(catalog);
  return upcoming.length ? upcoming[0] : null;
}

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"];

/**
 * «2026-07-25 11:42:46» → «25 июля». Разбираем СТРОКОЙ, а не через `new Date`:
 * ядро пишет момент в UTC, а разбор датой сдвинул бы день по часовому поясу телефона —
 * и награда, полученная поздно вечером, «переехала» бы на завтра.
 */
export function earnedDate(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw || ""));
  if (!m) return "";
  const day = Number(m[3]);
  const month = MONTHS[Number(m[2]) - 1] ?? "";
  return month ? `${day} ${month}` : "";
}
